/**
 * "Recently opened boards": a small, bounded, per-user list kept in localStorage - only board ids
 * and timestamps (no names, no content). It is a convenience, never proof of access: the Board
 * Deck shows an entry only when the user's CURRENT board list contains it.
 */
const MAX_RECENT = 30;

interface RecentEntry {
  id: string;
  at: number;
}

const keyFor = (userId: string) => `zadok.recentBoards.${userId}`;

function read(userId: string): RecentEntry[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(keyFor(userId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is RecentEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof (entry as RecentEntry).id === 'string' &&
        typeof (entry as RecentEntry).at === 'number',
    );
  } catch {
    return [];
  }
}

function write(userId: string, entries: RecentEntry[]): void {
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(entries.slice(0, MAX_RECENT)));
  } catch {
    /* storage unavailable: the list just is not remembered */
  }
}

/** Moves the board to the front (no duplicates, bounded). */
export function recordRecentBoard(userId: string, boardId: string): void {
  const rest = read(userId).filter((entry) => entry.id !== boardId);
  write(userId, [{ id: boardId, at: Date.now() }, ...rest]);
}

/** Board ids, most recent first. */
export function readRecentBoardIds(userId: string): string[] {
  return read(userId)
    .sort((a, b) => b.at - a.at)
    .map((entry) => entry.id);
}

/** Drops ids that are no longer in `keep` (deleted / inaccessible boards). */
export function pruneRecentBoards(userId: string, keep: ReadonlySet<string>): void {
  const entries = read(userId);
  const kept = entries.filter((entry) => keep.has(entry.id));
  if (kept.length !== entries.length) write(userId, kept);
}
